import { handleActions } from '@gef-ui/utils/fsaActions';
import path from "ramda/es/path";
import always from "ramda/es/always";
import append from "ramda/es/append";
import assoc from "ramda/es/assoc";
import assocPath from "ramda/es/assocPath";
import complement from "ramda/es/complement";
import lensPath from "ramda/es/lensPath";
import map from "ramda/es/map";
import mapObjIndexed from "ramda/es/mapObjIndexed";
import over from "ramda/es/over";
import prepend from "ramda/es/prepend";
import remove from "ramda/es/remove";
import alwaysEmptyArray from "ramda-extension/es/alwaysEmptyArray";
import pathNotEq from "ramda-extension/es/pathNotEq";
import { ReplaySubject, merge, pipe } from 'rxjs';
import { filter, map as rxMap, scan, shareReplay, withLatestFrom } from 'rxjs/operators';
import { getDeepPath, getTableRowPath, metaDeepPath, metaKeepData, payload, payloadValue } from '../../../utils/data';
import mergeDeepFormData from '../../../utils/mergeDeepFormData';
import { actionNames, initFormData } from '../../transfer/control';
import { bufferControl, enhanceWithResolvedFieldDescriptor, filterByActionType, skipWhenStateEqual } from '../../utils';
const PRIVATE_SET_DEFAULTS = 'PRIVATE_SET_DEFAULTS';
const passthroughActions$ = filterByActionType(actionNames.INIT_FORM_DATA, actionNames.APPEND_TO_LIST, actionNames.PREPEND_TO_LIST, actionNames.REMOVE_FROM_LIST, actionNames.CLEAR_LIST, actionNames.DEEP_MERGE_DATA, actionNames.REPLACE_DATA, actionNames.SET_VALUE_BY_DEEP_PATH);
const resetFormAction$ = (initialFormData$)=>pipe(filterByActionType(actionNames.RESET_FORM), filter(complement(metaKeepData)), withLatestFrom(initialFormData$), rxMap(([action, initialFormData])=>{
        if (action.payload) {
            // new initialFormData
            return action;
        }
        return assoc('payload', initialFormData, action);
    }));
const setValueAction$ = (formDescriptor$)=>pipe(filterByActionType(actionNames.SET_VALUE), enhanceWithResolvedFieldDescriptor(formDescriptor$));
const rowActions$ = (formDescriptor$)=>pipe(filterByActionType(actionNames.CLEAR_ROW, actionNames.DEEP_MERGE_ROW, actionNames.REPLACE_ROW), withLatestFrom(formDescriptor$), rxMap(([{ type, payload }, formDescriptor])=>({
            type,
            payload: {
                ...payload,
                deepPath: getTableRowPath(formDescriptor, payload.arrayName, payload.arrayIndices)
            }
        })));
const defaultAction$ = (control$, formDescriptor$)=>pipe(bufferControl(control$, filter(({ type, payload })=>type === actionNames.REGISTER && payload.defaultValue !== undefined && payload.defaultValue !== null)), withLatestFrom(formDescriptor$), rxMap(([bufferedActions, formDescriptor])=>{
        return {
            type: PRIVATE_SET_DEFAULTS,
            payload: bufferedActions.map((action)=>{
                return [
                    getDeepPath(formDescriptor, formDescriptor[action.meta.fieldName], action.meta.fieldIndices),
                    action.payload.defaultValue
                ];
            })
        };
    }));
const scanReducer = (reducerHandler)=>handleActions({
        [actionNames.INIT_FORM_DATA]: (_state, { payload })=>payload,
        [actionNames.RESET_FORM]: (_state, { payload })=>payload,
        [actionNames.SET_VALUE]: (state, action)=>{
            if (path(metaDeepPath(action), state) === payloadValue(action)) {
                return state;
            }
            return assocPath(metaDeepPath(action), payloadValue(action), state);
        },
        [actionNames.APPEND_TO_LIST]: (state, { payload })=>over(lensPath(payload.deepPath), append(payload.value), state),
        [actionNames.PREPEND_TO_LIST]: (state, { payload })=>over(lensPath(payload.deepPath), prepend(payload.value), state),
        [actionNames.REMOVE_FROM_LIST]: (state, { payload })=>over(lensPath(payload.deepPath), remove(payload.index, 1), state),
        [actionNames.CLEAR_LIST]: (state, { payload })=>over(lensPath(payload), alwaysEmptyArray, state),
        [actionNames.DEEP_MERGE_DATA]: (state, { payload })=>mergeDeepFormData(state, payload),
        [actionNames.REPLACE_DATA]: (_state, { payload })=>payload,
        [actionNames.CLEAR_ROW]: (state, { payload })=>over(lensPath(payload.deepPath), mapObjIndexed(always('')), state),
        [actionNames.DEEP_MERGE_ROW]: (state, { payload })=>over(lensPath(payload.deepPath), (row)=>mergeDeepFormData(row, payload.data), state),
        [actionNames.REPLACE_ROW]: (state, { payload })=>over(lensPath(payload.deepPath), always(payload.data), state),
        [PRIVATE_SET_DEFAULTS]: (state, { payload })=>{
            let newState = state;
            payload.forEach(([deepPath, value])=>{
                const existingValue = path(deepPath, newState);
                if (existingValue === undefined || existingValue === null) {
                    newState = assocPath(deepPath, value, newState);
                }
            });
            return newState;
        },
        [actionNames.SET_VALUE_BY_DEEP_PATH]: (state, { payload })=>{
            const { deepPath, value } = payload;
            if (deepPath && pathNotEq(value, deepPath, state)) {
                return assocPath(deepPath, value, state);
            } else {
                return state;
            }
        },
        ...reducerHandler
    }, {});
const defaultState = {};
export default (({ control$, formDescriptor$ }, { createInputs = [], reducerHandler = {} } = {}, initState = defaultState)=>{
    const extensionInputs$ = map((ext)=>ext({
            control$,
            formDescriptor$
        }), createInputs);
    const initialFormData$ = new ReplaySubject(1);
    merge(control$.pipe(filterByActionType(actionNames.INIT_FORM_DATA)), control$.pipe(filterByActionType(actionNames.RESET_FORM), filter(payload))).pipe(rxMap(payload)).subscribe(initialFormData$);
    const formData$ = merge(control$.pipe(passthroughActions$), control$.pipe(resetFormAction$(initialFormData$)), control$.pipe(setValueAction$(formDescriptor$)), control$.pipe(rowActions$(formDescriptor$)), control$.pipe(defaultAction$(control$, formDescriptor$)), ...extensionInputs$).pipe(scan(scanReducer(reducerHandler), {}), skipWhenStateEqual(), shareReplay(1));
    formData$.subscribe();
    // po prve se inicializuje pro skipWhenStateEqual. Urcite pujde vyresit lepe :)
    control$.next(initFormData({}));
    if (initState !== defaultState) {
        // form initialize itself in useForm, but if we already have data, then initialize it directly
        control$.next(initFormData(initState));
    }
    control$.pipe(filterByActionType(actionNames.RESET_FORM), filter(metaKeepData), withLatestFrom(formData$), rxMap(([, formData])=>formData)).subscribe(initialFormData$);
    return formData$;
});
